/*
 * File: RichTextViewer.tsx
 * Author: Austin Robinson
 * Date Created: 1/28/2025
 * Copyright (c) DrivingDoc 2025
 */
import { useEffect, useRef } from 'react';

import { AutoLinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { InitialConfigType, LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import Box from '@mui/material/Box';
import type { EditorThemeClasses, SerializedEditorState } from 'lexical';

import { MergeTagNode } from '../plugins';
import AutoLinkPlugin from '../plugins/AutoLinkPlugin';
import EditorTheme from '../themes/EditorTheme';
import ContentEditable from '../shared/ContentEditable';

/**
 * Props for the RichTextViewer component.
 *
 * @interface RichTextViewerProps
 */
export interface RichTextViewerProps {
  /**
   * The serialized editor state to render.
   *
   * @type {SerializedEditorState}
   */
  value: SerializedEditorState;

  /**
   * Custom Lexical editor theme.
   *
   * @type {EditorThemeClasses}
   */
  theme?: EditorThemeClasses;

  /**
   * Namespace for the Lexical editor.
   * Must be unique if multiple editors/viewers are on the same page.
   *
   * @type {string}
   */
  namespace?: string;

  /**
   * Horizontal padding (left and right).
   * Can be a number (converted to theme.spacing) or a string for custom CSS.
   *
   * @type {number | string}
   * @default 0
   */
  px?: number | string;

  /**
   * Vertical padding (top and bottom).
   * Can be a number (converted to theme.spacing) or a string for custom CSS.
   *
   * @type {number | string}
   * @default 0
   */
  py?: number | string;
}

/**
 * Internal component that handles the editor state hydration.
 */
function ViewerContent({ value }: { value: SerializedEditorState }) {
  const [editor] = useLexicalComposerContext();
  const hasInitialized = useRef(false);

  // Hydrate the editor state only once
  useEffect(() => {
    if (!hasInitialized.current && value) {
      const parsed = editor.parseEditorState(value);
      editor.update(() => {
        editor.setEditorState(parsed);
      });
      hasInitialized.current = true;
    }
  }, [value, editor]);

  return null;
}

/**
 * A read-only viewer component for rendering Lexical serialized editor state.
 * This component takes serialized editor state and renders it without any editing features.
 *
 * Features:
 * - Read-only rendering of rich text content
 * - Supports headings, lists, links, quotes, and merge tags
 * - No toolbar or editing capabilities
 * - Customizable theme and padding
 *
 * @example
 * ```tsx
 * // Basic usage
 * <RichTextViewer value={serializedState} />
 *
 * // With custom padding
 * <RichTextViewer
 *   value={serializedState}
 *   px={2}
 *   py={1}
 * />
 *
 * // With custom theme
 * <RichTextViewer
 *   value={serializedState}
 *   theme={customTheme}
 * />
 * ```
 *
 * @param {RichTextViewerProps} props - Component props
 * @returns {JSX.Element} The RichTextViewer component
 */
export function RichTextViewer({
  value,
  theme = EditorTheme,
  namespace = 'RichTextViewer',
  px = 0,
  py = 0,
}: RichTextViewerProps) {
  const initialConfig: InitialConfigType = {
    namespace: namespace,
    theme: theme,
    editable: false, // Make the editor read-only
    onError: console.error,
    nodes: [AutoLinkNode, HeadingNode, ListItemNode, ListNode, MergeTagNode, QuoteNode],
  };

  return (
    <Box>
      <LexicalComposer initialConfig={initialConfig}>
        <RichTextPlugin
          contentEditable={<ContentEditable px={px} py={py} />}
          ErrorBoundary={LexicalErrorBoundary}
          placeholder={null}
        />
        <ListPlugin />
        <AutoLinkPlugin />
        <ViewerContent value={value} />
      </LexicalComposer>
    </Box>
  );
}

export default RichTextViewer;
